import moment from 'moment';
import { ActionType, ActionUpdatePayload, ChecklistInbound, ChecklistOutbound, LegacyTransportType, LocationType, Machine, StatusType, Stop, Tour, TourStopAction, TourStopSource, TransformedChecklist } from '@models/*';
import { OverviewState } from '@overviewstore/*';
import { cloneDeep } from 'lodash';

export const IMAGE_DATA_SANITIZED = 'SANITIZED IMAGE BASE64';

/**
 * Takes an array of Tours as argument
 * Returns an immutable copy of the tour array sorted by 3 chained criteria: by day, by type, and by intra-day time
 * CURRENTLY USED FOR EXTRACTING DERIVED DATA IN SELECTORS
 */
export const sortTours = (tours: Tour[]): Tour[] => {
    if (!tours || !tours.length) {
        return [];
    }

    const ranking = {
        [StatusType.COMPLETED]: 1,
        [StatusType.INPROGRESS]: 2,
        [StatusType.NEW]: 3,
        [StatusType.CANCELLED]: 4,
    };

    return [...tours].sort((first, second) => {
        const firstT = moment(first.startDate);
        const secondT = moment(second.startDate);
        const diffTime = firstT.diff(secondT);
        const diffDays = firstT.startOf('day').diff(secondT.startOf('day'), 'days');
        const diffRanking = ranking[first.status] - ranking[second.status];

        if (diffDays) {
            return diffDays;
        }
        if (diffRanking) {
            return diffRanking;
        }
        return diffTime;
    });
};

/**
 * Takes a single tour as argument and sorts its nested data:
 * Within each tour, it sorts its stops by sequence
 * Withing each stop, it sorts its actions by sequence
 * Returns an immutable copy of the changed tour
 * CURRENTLY USED IN REDUCERS FOR PROCESSING DATA BEFORE STORING IN STATE MANAGER
 */
export const sortNestedTourData = (tour: Tour): Tour => {
    return {
        ...tour,
        stops: [...tour.stops]
            .sort((first: Stop, second: Stop) => first.sequence - second.sequence)
            .map((stop: Stop) => {
                return {
                    ...stop,
                    actions: [...stop.actions].sort((first: TourStopAction, second: TourStopAction) => first.sequence - second.sequence)
                };
            })
    };
};

/*
 * Takes a single TSA as argument and returns the key to get corresponding driver notes
 * based on the composite key from the transport, which combines order reference, cargoInstanceId or cargoDescription
 * the string contains one of these combinations:
 * `"orderReference",0,"cargoInstanceId"`
 * `"orderReference",1,"cargoDescription"`
 */
export const getDriverNotesKeyForTSA = (action: TourStopAction): string => {
    const { orderReference, instanceId, cargoDescription } = action.transport;
    let key = `"${orderReference}",`;
    if (instanceId) {
        key += `0,"${instanceId}"`;
    } else {
        key += `1,"${cargoDescription}"`;
    }
    return key;
};

/**
 * sets (load)tour_stop_action.unloadAddress for display in the UI.
 * @param tour
 */
export const setUnloadAddresses = (tour: Tour) => {

    const loadActionsByTransportId = {};
    const unloadAddressesByTransportId = {}

    for (let stop of tour.stops) {
        for (let action of stop.actions) {
            if (action.actionType === ActionType.LOAD) { loadActionsByTransportId[action.transportId] = action }
            if (action.actionType === ActionType.UNLOAD) {
                let unLoadAddress = null;
                if (stop.locationType === LocationType.DEPOT) {
                    unLoadAddress = stop.name;
                } else {
                    unLoadAddress = [stop.name, stop.address].join(", ");
                }

                unloadAddressesByTransportId[action.transportId] = unLoadAddress;
            }
        }
    }

    let transportId: any = null;
    let loadAction: any = null;

    for ([transportId, loadAction] of Object.entries(loadActionsByTransportId)) {
        loadAction.unloadAddress = unloadAddressesByTransportId[transportId]
    }

    return tour;
};

export const isPrevDaysTour = (tour: Tour): boolean => {
    const today = moment().startOf('day');
    const startDate = moment(tour.startDate).startOf('day');

    return (
        (startDate.diff(today, 'days') < -1) ||
        (
            isYesterdayOnlyTour(tour) &&
            ((tour.status === StatusType.NEW) || (tour.status === StatusType.INPROGRESS))
        )
    );
};

export const isYesterdayOnlyTour = (tour: Tour): boolean => {
    const today = moment().startOf('day');
    const startDate = moment(tour.startDate).startOf('day');
    const endDate = moment(tour.endDate).startOf('day');

    return (
        (startDate.diff(today, 'days') === -1) &&
        (endDate.diff(today, 'days') === -1)
    );
};

/**
 * Takes a tours entity map and an array of objects which represent actions and their corresponding updates as arguments
 * Returns an immutable copy of the tour entity map with the received actions and their updates applied
 * Updates can so far be: action.statusType and action.machineRasterLocation
 * CURRENTLY USED IN REDUCERS FOR ATTACHING AN UPDATE TO DEEPLY NESTED TOUR STOP ACTIONS
 */
export const attachUpdates = (
    tourEntities: { [id: number]: Tour },
    updateActions: ActionUpdatePayload[]
): { [id: number]: Tour } => {
    const idsToUpdate = updateActions.map(elem => elem.tourStopActionId);

    return Object.keys(tourEntities).reduce((accum, key) => {
        return {
            ...accum,
            [key]: {
                ...tourEntities[key],
                stops: [...tourEntities[key].stops].map((stop: Stop) => {
                    return {
                        ...stop,
                        actions: [...stop.actions].map((action: TourStopAction) => {
                            if (idsToUpdate.includes(action.id)) {
                                const actionToUpdate = updateActions.find(elem => elem.tourStopActionId === action.id);
                                const status = actionToUpdate.status;
                                const machineRasterLocation = actionToUpdate.rasterLocation || action.machineRasterLocation;
                                return {
                                    ...action,
                                    machineRasterLocation,
                                    status
                                };
                            } else {
                                return {
                                    ...action
                                };
                            }
                        })
                    };
                })
            }
        };
    }, {});
};

// todo optimize finalizing algorithm

/**
 * Takes a tours entity map as an argument
 * Iterates through all 3 levels of the tour object (tour/stop/action) and recalculates finalize status for each
 * Returns an immutable copy of the tour entity map with the updates applied
 * CURRENTLY USED IN REDUCERS FOR DERIVING FINALIZED STATUS FOR TOURS AND TOUR STOPS
 */
export const recompileFinalizedStatuses = (tourEntities: { [id: number]: Tour }): { [id: number]: Tour } => {
    const newTourEntities = cloneDeep(tourEntities);

    Object.keys(newTourEntities).forEach(key => {
        const tour = newTourEntities[key];

        if (tour.status === StatusType.CANCELLED) {
            return;
        }

        tour.stops.forEach((tourStop: Stop) => {
            const completedActions = tourStop.actions.filter((action: TourStopAction) => {
                return (
                    action.status === StatusType.COMPLETED ||
                    action.status === StatusType.CANCELLED ||
                    action.status === StatusType.COMPLETED_PLANNER
                );
            });

            if (completedActions.length === tourStop.actions.length) {
                tourStop.status = StatusType.COMPLETED;
            }
        });

        const completedStops = tour.stops.filter((tourStop: Stop) => {
            return tourStop.status === StatusType.COMPLETED;
        });

        if (completedStops.length === tour.stops.length) {
            tour.status = StatusType.COMPLETED;
        }
    });

    return newTourEntities;
};

/**
 * Takes a tours entity map and an object representing the needed data for a fuel refill update
 * Returns an immutable copy of the tour entity map with the received update applied to the respective action's
 * transport CURRENTLY USED IN REDUCERS FOR ATTACHING A REFILL UPDATE TO DEEPLY NESTED TOUR STOP ACTIONS
 */
export const attachFuelRefill = (
    tourEntities: { [id: number]: Tour },
    updateDetails: { tourStopActionId: number, amount: number, time: string }
): { [id: number]: Tour } => {
    return Object.keys(tourEntities).reduce((accum, key) => {
        return {
            ...accum,
            [key]: {
                ...tourEntities[key],
                stops: [...tourEntities[key].stops].map((stop: Stop) => {
                    return {
                        ...stop,
                        actions: [...stop.actions].map((action: TourStopAction) => {
                            if (updateDetails.tourStopActionId === action.id) {
                                return {
                                    ...action,
                                    transport: {
                                        ...action.transport,
                                        fuelRefillTime: updateDetails.time,
                                        fuelRefillAmount: updateDetails.amount
                                    }
                                };
                            } else {
                                return {
                                    ...action
                                };
                            }
                        })
                    };
                })
            }
        };
    }, {});
};

/**
 * Takes a tours entity map and an object containing an actionId and a machine as parameters
 * Returns an immutable copy of the tour entity map with the received action's machine replaced with the one received
 * via param CURRENTLY USED IN REDUCERS FOR ATTACHING A MACHINE SWITCH UPDATE TO DEEPLY NESTED TOUR STOP ACTIONS
 */
export const attachNewMachine = (
    tourEntities: { [id: number]: Tour },
    updateDetails: {
        oldMachineInternalId: string,
        newMachine: Machine,
    }
): { [id: number]: Tour } => {
    return Object.keys(tourEntities).reduce((accum, key) => {
        return {
            ...accum,
            [key]: {
                ...tourEntities[key],
                stops: [...tourEntities[key].stops].map((stop: Stop) => {
                    return {
                        ...stop,
                        actions: [...stop.actions].map((action: TourStopAction) => {
                            if (action.transport.machine && action.transport.machine.internalId === updateDetails.oldMachineInternalId) {
                                return {
                                    ...action,
                                    transport: {
                                        ...action.transport,
                                        machine: updateDetails.newMachine,
                                    }
                                };
                            } else {
                                return {
                                    ...action
                                };
                            }
                        })
                    };
                })
            }
        };
    }, {});
};

/**
 * Takes a list of tour entities and a ContractDetailId
 * returns a list of tour stop action id's
 */
export const getTSAIdsForContractDetailId = (entities: Tour[], contractDetailId) => {
    return entities.reduce((actionIds: number[], entity) => {
        return [...actionIds, ...entity.stops.reduce((stopActionIds, s) => {
            return [...stopActionIds, ...s.actions.reduce((ids, tsa) => {
                if (tsa && tsa.transport && tsa.transport.contractDetail
                    && tsa.transport.contractDetail.internalId === contractDetailId) {
                    ids.push(tsa.id);
                }
                return ids;
            }, [])];
        }, [])];
    }, []);
};

/**
 * Used in the preload stage, when tours are being screened for actions whose checklist can benefit from crosschecking
 * logic Takes an array of Tours as argument Parses the given tours to extract the actions which have locationType
 * CUSTOMER and transportType PICKUP For each of these actions, a corresponding payload object is generated Returns an
 * array of said objects
 */
export const extractCrosscheckPayloads = (tours: Tour[]): any[] => {

    if (!!!tours) {
        return [];
    }
    return tours.reduce((tourAcc, tour) => {
        return tourAcc.concat(
            tour.stops.reduce((stopAcc, stop) => {
                return stopAcc.concat(
                    stop.actions
                        .filter((action: TourStopAction) => action.transport && action.transport.machine && action.transport.contractDetail)
                        .filter((action: TourStopAction) => {
                            return stop.locationType === LocationType.CUSTOMER && action.transport.transportType === LegacyTransportType.PICKUP;
                        })
                        .map((action: TourStopAction) => {
                            return {
                                tourStopActionId: action.id,
                                machineId: action.transport.machine.id,
                                contractDetailInternalId: action.transport.contractDetail.internalId,
                                transportType: LegacyTransportType.DELIVERY
                            };
                        })
                );
            }, [])
        );
    }, []);
};

/**
 * Takes a single raw checklist object, either received from an endpoint or generated from inside the app
 * Returns a single processed checklist data object for prefilling pickup protocols
 * Trims away all unnecessary data, leaving only the dynamic segment of the checklist
 * Modifies the checklist elements themselves as to prepare them for use in the protocol data bindings
 */
export const transformChecklist = (checklist: ChecklistInbound | ChecklistOutbound): TransformedChecklist => {
    return checklist.checklist.reduce((transformedSections, section) => {
        return {
            ...transformedSections,
            [section.name]: section.items.reduce((transformedFields, field) => {
                // replace boolean string by boolean value

                let realFieldValue;
                if (field.type === 'boolean') {
                    realFieldValue = (typeof field.value === 'string') ? JSON.parse(field.value) : field.value;
                } else {
                    realFieldValue = field.value;
                }

                const transformedField = {
                    value: realFieldValue,
                    comments: field.comments
                };

                return {
                    ...transformedFields,
                    [uiFieldName(field.name)]: transformedField
                };
            }, {})
        };
    }, {});
};

/**
 *
 * @returns field name as used in the UI, thus the CHECKLIST_ prefix added for persistence is removed and the entire
 *   name is lower-cased.
 */
function uiFieldName(fieldnameFromDB: string): string {
    return fieldnameFromDB.substring(10, fieldnameFromDB.length).toLowerCase();
}

/**
 * Function that does the heavy lifting of adapting the raw shift data received via API to a model usable in the UI
 * Does the following operations:
 * - time ordering for shifts,
 * - inside each shift, time ordering for tour stop trackers and shift breaks,
 * - extraction of tracked tours and tour stops from the data and ordering for them,
 * - concatenation of tour stop strackers and tour stop snapshots in the same genericStop property,
 * - extraction of total duration of shifts and total duration of breaks withing shifts
 * CURRENTLY IT IS ONLY USED IN THE REPORT SHIFT SELECTOR, AS STORING IT IN THE DATA MODEL IS UNNECESSARY
 */
export const processReportData = shifts => {
    const sortByTime = (first, second) => {
        const firstTime = moment(first.startedAt);
        const secondTime = moment(second.startedAt);

        return firstTime.diff(secondTime);
    };

    return shifts
        .sort(sortByTime)
        .map(shift => {
            const orderedTourStopTrackings = shift.tourStopTrackings ?
                [...shift.tourStopTrackings].sort(sortByTime) :
                [];

            const orderedTours = orderedTourStopTrackings
                .reduce((toursWithTSTs, tourStopTracking) => {
                    const existingTourWithTST = toursWithTSTs.find(twtst => twtst.id === tourStopTracking.tour.id);
                    const currTourWithTST = existingTourWithTST ? { ...existingTourWithTST } : { ...tourStopTracking.tour };

                    return [
                        ...toursWithTSTs.filter(twtst => twtst.id !== currTourWithTST.id),
                        {
                            ...currTourWithTST,
                            tourStopTrackings: existingTourWithTST ? [...currTourWithTST.tourStopTrackings, { ...tourStopTracking }] : [{ ...tourStopTracking }]
                        }
                    ];
                }, [])
                .sort((first, second) => {
                    const firstTime = moment(first.tourStopTrackings[0].startedAt);
                    const secondTime = moment(second.tourStopTrackings[0].startedAt);

                    return firstTime.diff(secondTime);
                });

            const orderedBreaks = [...shift.shiftBreaks].sort(sortByTime);

            Object.assign(
                shift,
                { orderedBreaks },
                { orderedTours },
                { orderedTourStopTrackings: orderedTourStopTrackings }
            );

            if (shift.endedAt) {
                const shiftStart = moment(shift.startedAt).startOf('minute');
                const shiftEnd = moment(shift.endedAt).startOf('minute');
                const durationShift = shiftEnd.diff(shiftStart, 'minutes');
                Object.assign(shift, {
                    totalDuration: {
                        hours: Math.floor(durationShift / 60),
                        minutes: durationShift % 60
                    }
                });
            }

            if (orderedBreaks.length) {
                let breakMinutes = 0;

                orderedBreaks.forEach(item => {
                    if (item.endedAt) {
                        const breakStart = moment(item.startedAt).startOf('minute');
                        const breakEnd = moment(item.endedAt).startOf('minute');
                        breakMinutes += breakEnd.diff(breakStart, 'minutes');
                    }
                });

                if (breakMinutes > 0) {
                    Object.assign(shift, {
                        totalBreaksDuration: {
                            hours: Math.floor(breakMinutes / 60),
                            minutes: breakMinutes % 60
                        }
                    });
                }
            }

            const { createdAt, updatedAt, ...processedShift } = shift;

            return processedShift;
        });
};

/**
 * Takes a tours entity map and an object containing an actionId and a raster location as parameters
 * Returns an immutable copy of the tour entity map with the
 * received action's machine's raster location replaced with the one received via param
 * CURRENTLY USED IN REDUCERS FOR UPDATING A MACHINE'S RASTER LOCATION WHEN THE APPROPRIATE SOCKET MESSAGE IS RECEIVED
 */
export const attachNewRasterLocation = (
    tourEntities: { [id: number]: Tour },
    updateData: { tourStopActionId: number, rasterLocation: string }
): { [id: number]: Tour } => {
    return Object.keys(tourEntities).reduce((accum, key) => {
        return {
            ...accum,
            [key]: {
                ...tourEntities[key],
                stops: [...tourEntities[key].stops.map(stop => {
                    return {
                        ...stop,
                        actions: [...stop.actions.map(action => {
                            return {
                                ...action,
                                transport: action.transport && {
                                    ...action.transport,
                                    machine: action.transport.machine && {
                                        ...action.transport.machine,
                                        rasterLocation: (action.id === updateData.tourStopActionId)
                                            ? updateData.rasterLocation : action.transport.machine.rasterLocation
                                    }
                                }
                            };
                        })]
                    };
                })]
            }
        };
    }, {});
};

/**
 * Used for removing the base64 representations of photos and damagePhotos that are uploaded in protocols,
 * while they are in progress, before submitting
 * The reason being that these files are too large for the purpose of persisting the whole state in localstorage
 * between reloads, for offline availability or for redux devtools processing
 */
export const SanitizeStateForStorage = (reducedState: OverviewState): OverviewState => {
    if (!reducedState) {
        return null;
    }

    const progChecklists = reducedState.checklists.inProgressChecklists;
    const trimmedProgChecklists = Object.keys(progChecklists).reduce((accum, key) => {
        return {
            ...accum,
            [key]: {
                ...progChecklists[key],
                checkupPhotos: progChecklists[key].checkupPhotos.map((photo) => ({ ...photo, data: IMAGE_DATA_SANITIZED })),
                damagePhotos: progChecklists[key].damagePhotos.map((photo) => ({ ...photo, data: IMAGE_DATA_SANITIZED })),
            },
        };
    }, {});

    return {
        ...reducedState,
        checklists: {
            ...reducedState.checklists,
            inProgressChecklists: trimmedProgChecklists,
        }
    };
};

export const validateAction = (tour: Tour, stop: Stop, action: TourStopAction): {
    valid: boolean,
    actionToCompleteFirst: TourStopAction | null,
    actionToCompleteFirstType: string,
    actionToCompleteFirstLocation: string,
} => {
    if (!tour || !stop || !action || stop.source === TourStopSource.DRIVE /* No validation for 'Unplanned' stop( Qdrive stop) */) {
        return { valid: true, actionToCompleteFirst: null, actionToCompleteFirstType: '', actionToCompleteFirstLocation: '' };
    }


    const orderedTourStopActions = tour.stops.reduce((actions, tourStop) => {

        // INFO: We add tour stop to the action(copy) to be able to use for the validation msg.
        const actionsWithTourStop = tourStop.actions.map((action: any) => {
            const actionCopy = Object.assign({}, action);
            actionCopy.stop = tourStop;
            return actionCopy;
        });

        return actions.concat(actionsWithTourStop);
    }, []);


    const selectedTourStopActionIndex = orderedTourStopActions.findIndex((tsa) => tsa.id === action.id);
    const previousTourStopActions = selectedTourStopActionIndex == -1 ? [] : orderedTourStopActions.slice(0, selectedTourStopActionIndex);

    const previousIncompleteRelatedActions = previousTourStopActions
        .filter(
            (tsa: TourStopAction) => {
                let isRelatedTransport;

                if (!!action.transport.machine) {
                    isRelatedTransport = !!tsa.transport.machine && action.transport.machine.id === tsa.transport.machine.id;
                } else {
                    isRelatedTransport = action.transport.id === tsa.transport.id; // INFO: We assume non machine tour stop action are related by transport ID (eg: Cargo Transport)
                }
                return (tsa.status === StatusType.NEW && isRelatedTransport);
            }
        );


    const validationResult: any = {
        valid: !previousIncompleteRelatedActions.length,
        actionToCompleteFirst: previousIncompleteRelatedActions.length ? previousIncompleteRelatedActions[0] : null
    };

    // Add properties for an easy display in the UI.

    validationResult.actionToCompleteFirstType = validationResult.valid ? '' : validationResult.actionToCompleteFirst.actionType;
    validationResult.actionToCompleteFirstLocation = validationResult.valid ? '' : validationResult.actionToCompleteFirst.stop.name;


    return validationResult;
};

export const isUnloadActionValid = (tour: Tour, stop: Stop, action: TourStopAction): boolean => {
    if (!tour || !stop || !action) {
        return true;
    }

    if (
        (action.actionType !== ActionType.UNLOAD) ||
        (action.status !== StatusType.NEW && action.status !== StatusType.INPROGRESS)
    ) {
        return true;
    }

    const hasPrevUnfinLoad = tour
        .stops
        .filter(item => item.sequence < stop.sequence)
        .reduce((accum, item) => accum.concat(item.actions), [])
        .some(item =>
            (item.actionType === ActionType.LOAD) &&
            (item.status === StatusType.NEW || item.status === StatusType.INPROGRESS) &&
            (
                (item.transport && item.transport.machine && action.transport.machine && item.transport.machine.internalId === action.transport.machine.internalId) ||
                (item.transport && item.transport.instanceId === null && item.transport.cargoDescription === action.transport.cargoDescription)

            )
        );

    return !hasPrevUnfinLoad;
};
